import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  CardMedia,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  Grid,
  Typography,
  Box,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';

import logo from 'assets/images/logo.svg';
import logoFull from 'assets/images/logo-mindweal.svg';
import logout from 'assets/images/log-out.svg';
import messages from 'assets/images/messages.svg';
import tasklist from 'assets/images/tasklist.svg';
import convolist from 'assets/images/convolist.svg';
import questionmark from 'assets/images/questionmark.svg';
import userIcon from 'assets/images/user.svg';
import patientDB from 'assets/images/patientDB.png';

import referralDB from 'assets/images/referralDB.png';
import mwiseDB from 'assets/images/mwiseDB.png';
import healthMonitoring from 'assets/images/health-monitoring.svg';
import health from 'assets/images/health.svg';
import review from 'assets/images/review.svg';
import mislenious from 'assets/images/mislenious.svg';
import Referal from 'assets/images/Referal.svg';
import MenuIcon from '@mui/icons-material/Menu';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import SyncProblemOutlinedIcon from '@mui/icons-material/SyncProblemOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import SettingsIcon from '@mui/icons-material/Settings';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { ROUTE } from 'route/RouteEnums';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'utils/AuthContext';
import COLORS from 'theme/colors';
import { useAppDispatch } from 'store/hooks';
import { getSibling } from 'store/slices/userSlice';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Collapse } from '@mui/material';
import SearchBox from 'utils/searchBox';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SiblingAvatar from 'components/SiblingSwitch/SiblingAvatar';
import { SiblingProfile, findActiveSibling, formatSiblingName } from 'components/SiblingSwitch/siblingProfile';
import { useSwitchSibling } from 'components/SiblingSwitch/useSwitchSibling';

/**
 * MM-1131: guardian "Settings" drawer item (Change Password / Change PIN), kept out of the drawer
 * render so it does not add to its complexity.
 * hidden - true for admins, who have no guardian Settings page
 * onOpen - navigates to Settings and closes the drawer
 */
const SettingsMenuItem = ({ hidden, onOpen }: { hidden: boolean; onOpen: () => void }) => {
  if (hidden) return null;
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onOpen}>
        <ListItemIcon>
          <SettingsIcon sx={{ color: COLORS.DarkBlue }} />
        </ListItemIcon>
        <ListItemText primary='Settings' />
      </ListItemButton>
    </ListItem>
  );
};

const Header = () => {
  const [openMisslenious, setOpenMisslenious] = useState(false);
  const [openMdac, setOpenMdac] = useState(false);
  const [openJobs, setOpenJobs] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [siblings, setSiblings] = useState<any>();
  /** MM-1123: Switch Child item the sibling pop-out menu is anchored to; null while closed. */
  const [siblingMenuAnchor, setSiblingMenuAnchor] = useState<HTMLElement | null>(null);
  const [openPracticeSetup, setOpenPracticeSetup] = useState(false);
  /** MM-1168: whether Practice Setup's "Offices & Locations" group (Manage Offices, Sync Calendar) is expanded. */
  const [openOfficesLocations, setOpenOfficesLocations] = useState(false);
  const [openDailyOperations, setOpenDailyOperations] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, setUserDetails } = useContext(AuthContext);
  /** MM-1213: patient open right now, so a sibling fetch that outlives a child switch can tell. */
  const latestPatientId = useRef(user?.patientId);
  latestPatientId.current = user?.patientId;
  /** MM-1123: switches child and lands on the dashboard. */
  const switchSibling = useSwitchSibling();
  /** MM-1123: child whose profile is open, ticked in the Switch Child menu. */
  const activeSibling = findActiveSibling(siblings, user?.patientId);
  /** MM-1123: Switch Child is enabled only when there is another child to switch to. */
  const canSwitchSibling: boolean = !user?.isAdmin && (siblings?.length ?? 0) > 1;

  /**
   * MM-1123: why Switch Child is disabled (shown as its tooltip), or '' when it can be used.
   *
   * @returns the tooltip text; non-empty means the item is disabled.
   */
  const getSwitchChildDisabledReason = (): string => {
    if (!canSwitchSibling) {
      return 'Add another child to switch between children.';
    }
    // MM-1123: also allowed on the appointment (self-scheduling) screens. Switching always lands on the
    // chosen child's dashboard, so the scheduling screen is left and never shows another child's slots.
    return '';
  };
  /** MM-1123: tooltip for Switch Child; non-empty while the item is disabled. */
  const switchChildDisabledReason = getSwitchChildDisabledReason();
  /** Drawer links; MM-1123 moved Add Child out to the grouped footer, so disabled/state are optional. */
  const menuItems: { label: string; link: string; icon: JSX.Element; disabled?: boolean; state?: any }[] = [
    {
      label: 'Dashboard',
      link: ROUTE.DASHBOARD,
      icon: <GridViewOutlinedIcon sx={{ color: COLORS.DarkBlue }} />,
    },
    {
      label: 'Care',
      link: ROUTE.PROFILE_CARE,
      icon: <MedicalServicesIcon sx={{ color: COLORS.DarkBlue }} />,
    },
    {
      label: 'Knowledge Base',
      link: 'https://kb.mindweal.com/en/publickb',
      icon: <MenuBookOutlinedIcon sx={{ color: COLORS.DarkBlue }} />,
    },
    {
      label: 'Documents',
      link: ROUTE.DOCUMENTS,
      icon: <PictureAsPdfOutlinedIcon sx={{ color: COLORS.DarkBlue }} />,
    },
    {
      label: 'Billing',
      link: ROUTE.PROFILE_BILLING,
      icon: <ReceiptLongIcon sx={{ color: COLORS.DarkBlue }} />,
    },
    // MM-1131: Manage Profile sits above Account Management.
    {
      label: 'Manage Profile',
      link: ROUTE.PATIENT_INFORMATION,
      icon: <AccountCircleIcon sx={{ color: COLORS.DarkBlue }} />,
    },
    {
      label: 'Account Management',
      link: ROUTE.PROFILE_ACCOUNT,
      icon: <ManageAccountsIcon sx={{ color: COLORS.DarkBlue }} />,
    },
  ];

  useEffect(() => {
    if (!user?.isAdmin && user?.patientId !== undefined) {
      getSiblings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.patientId]);

  const getSiblings = async () => {
    const requestedPatientId = user?.patientId;
    await dispatch(getSibling({}))
      .unwrap()
      .then(async (response: any) => {
        // MM-1213: the guardian switched child while this fetch ran; writing this stale user back
        // would reopen the previous child. The fetch started by the switch takes over.
        if (latestPatientId.current !== requestedPatientId) {
          return;
        }
        if (response?.status === 200) {
          setSiblings(response?.data);
          const name = response?.data?.find((s: any) => s.id === user?.patientId);
          if (user?.addedNewPatient) {
            CometChatUIKit.logout().then((res: any) => {
              if (res.success) {
                CometChatUIKit.login(name?.cometUserId);
              }
            });
          }
          delete user.addedNewPatient;
          const data = { ...user, patientId: name?.id, secureMessageUserId: name?.cometUserId };
          setUserDetails(data);
        }
      });
  };

  const hasPermission = (perm: string) => user?.permissions?.includes(perm);

  const menulist = () => (
    <Grid
      container
      // MM-1123: minHeight + nowrap so a tall menu (grouped footer, expanded sibling list) scrolls on short
      // mobile screens instead of wrapping the footer into a second, overlapping column.
      sx={{ width: 250, minHeight: '100%' }}
      role='presentation'
      flexDirection={'column'}
      flexWrap={'nowrap'}
      justifyContent={'space-between'}
      position={'relative'}
    >
      <Grid item>
        <List>
          {user.isAdmin ? (
            <>
              {/* Admin section: Dashboard */}
              {hasPermission('VIEW_PATIENTS') ? (
                <ListItem key={'Dashboard'} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate(`${ROUTE.ADMIN_DASHBOARD}`);
                      setDrawer(false);
                    }}
                  >
                    <ListItemIcon>
                      <GridViewOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
                    </ListItemIcon>
                    <ListItemText primary={'Dashboard'} />
                  </ListItemButton>
                </ListItem>
              ) : null}
              {/* Admin section: Calendar */}
              {hasPermission('MANAGE_CLINICAL_VISITS') ? (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate(`${ROUTE.ADMIN_APPOINTMENTS}`);
                      setDrawer(false);
                    }}
                  >
                    <ListItemIcon>
                      <CalendarMonthOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
                    </ListItemIcon>
                    <ListItemText primary='Calendar' />
                  </ListItemButton>
                </ListItem>
              ) : null}

              {/* Admin section: Account Management Requests */}
              {hasPermission('ACCOUNT_MANAGEMENT') ? (
                <ListItem key={'account'} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate(`${ROUTE.ADMIN_ACCOUNT_MANAGEMENT_REQUEST}`);
                      setDrawer(false);
                    }}
                  >
                    <ListItemIcon>
                      <CardMedia
                        component={'img'}
                        alt='Account Management'
                        src={questionmark}
                        sx={{ width: '24px', height: 'auto' }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={'Account Management'} />
                  </ListItemButton>
                </ListItem>
              ) : null}
              {/* Admin section: Daily Operations */}
              {hasPermission('SECURE_MESSAGE') ||
              hasPermission('MANAGE_TASK') ||
              hasPermission('CONVERSATIONS_INSIGHT') ||
              hasPermission('REVIEW_DOCUMENTS') ? (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => setOpenDailyOperations(!openDailyOperations)}>
                      <ListItemIcon>
                        <CorporateFareIcon sx={{ color: COLORS.DarkBlue }} />
                      </ListItemIcon>
                      <ListItemText primary='Daily Operations' />
                      {openDailyOperations ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={openDailyOperations} timeout='auto' unmountOnExit>
                    <List>
                      {/* Daily Operations sub-item: Messages */}
                      {hasPermission('SECURE_MESSAGE') ? (
                        <ListItem sx={{ pl: 2 }} key={'messages'} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              navigate(`${ROUTE.ADMIN_MESSAGES}`);
                              setDrawer(false);
                            }}
                          >
                            <ListItemIcon>
                              <CardMedia
                                component={'img'}
                                alt='messages'
                                src={messages}
                                sx={{ width: '24px', height: 'auto' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary={'Messages'} />
                          </ListItemButton>
                        </ListItem>
                      ) : null}
                      {/* Daily Operations sub-item: Conversation List */}
                      {hasPermission('CONVERSATIONS_INSIGHT') ? (
                        <ListItem sx={{ pl: 2 }} key={'conversationList'} disablePadding>
                          <ListItemButton
                            sx={{ pl: 2 }}
                            onClick={() => {
                              navigate(`${ROUTE.ADMIN_CONVERSATION_LIST}`);
                              setDrawer(false);
                            }}
                          >
                            <ListItemIcon>
                              <CardMedia
                                component={'img'}
                                alt='Conversation List'
                                src={convolist}
                                sx={{ width: '24px', height: 'auto' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary={'Conversation List'} />
                          </ListItemButton>
                        </ListItem>
                      ) : null}
                      {/* Daily Operations sub-item: Task List */}
                      {hasPermission('MANAGE_TASK') ? (
                        <ListItem sx={{ pl: 2 }} key={'tasklist'} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              navigate(`${ROUTE.ADMIN_TASKLIST}`);
                              setDrawer(false);
                            }}
                          >
                            <ListItemIcon>
                              <CardMedia
                                component={'img'}
                                alt='tasklist'
                                src={tasklist}
                                sx={{ width: '24px', height: 'auto' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary={'Task List'} />
                          </ListItemButton>
                        </ListItem>
                      ) : null}
                      {hasPermission('REVIEW_DOCUMENTS') ? (
                        <ListItem sx={{ pl: 2 }} key={'documentsReview'} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              navigate(`${ROUTE.ADMIN_DOCUMENT_REVIEW}`);
                              setDrawer(false);
                            }}
                          >
                            <ListItemIcon>
                              <PictureAsPdfOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
                            </ListItemIcon>
                            <ListItemText primary={'Documents'} />
                          </ListItemButton>
                        </ListItem>
                      ) : null}
                    </List>
                  </Collapse>
                </>
              ) : null}
              {/* Admin section: MDAC */}
              {hasPermission('PATIENT_ONBOARDING_DATABASE') ||
              hasPermission('MANAGE_REFPOD') ||
              hasPermission('PATIENT_DATABASE') ||
              hasPermission('MWISE_FEEDBACK') ||
              hasPermission('MW_ANALYTICS_REF_SOURCE') ? (
                <ListItem disablePadding>
                  <ListItemButton onClick={() => setOpenMdac(!openMdac)}>
                    <ListItemIcon>
                      <CardMedia
                        component={'img'}
                        alt='mdac'
                        src={userIcon} // Replace with your actual image
                        sx={{ width: '24px', height: 'auto' }}
                      />
                    </ListItemIcon>
                    <ListItemText primary='MDAC' />
                    {openMdac ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
              ) : null}

              <Collapse in={openMdac} timeout='auto' unmountOnExit>
                <List component='div' disablePadding>
                  {hasPermission('PATIENT_DATABASE') ? (
                    <ListItem key={'patientReport'} disablePadding>
                      <ListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => {
                          navigate(ROUTE.ADMIN_PATIENT_REPORT);
                          setDrawer(false);
                        }}
                      >
                        <ListItemIcon>
                          <CardMedia
                            component={'img'}
                            alt='Patient Database'
                            src={patientDB} // Replace with your actual image
                            sx={{ width: '35px', height: 'auto' }}
                          />
                        </ListItemIcon>
                        <ListItemText primary='Patient Database' />
                      </ListItemButton>
                    </ListItem>
                  ) : null}
                  {hasPermission('PATIENT_ONBOARDING_DATABASE') ? (
                    <ListItem key={'patientOnboarding'} disablePadding>
                      <ListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => {
                          navigate(ROUTE.ADMIN_PATIENT_ONBOARDING);
                          setDrawer(false);
                        }}
                      >
                        <ListItemIcon>
                          <CardMedia
                            component={'img'}
                            alt='Patient Onboarding Database'
                            src={patientDB} // Replace with your actual image
                            sx={{ width: '35px', height: 'auto' }}
                          />
                        </ListItemIcon>
                        <ListItemText primary='Patient Onboarding Database' />
                      </ListItemButton>
                    </ListItem>
                  ) : null}
                  {hasPermission('MANAGE_REFPOD') ? (
                    <ListItem key={'officelist'} disablePadding>
                      <ListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => {
                          navigate(ROUTE.ADMIN_OFFICE_LIST);
                          setDrawer(false);
                        }}
                      >
                        <ListItemIcon>
                          <CardMedia
                            component={'img'}
                            alt='Referral Provider Organization Database'
                            src={referralDB} // Replace with your actual image
                            sx={{ width: '35px', height: 'auto' }}
                          />
                        </ListItemIcon>
                        <ListItemText primary='Referral Provider Organization Database' />
                      </ListItemButton>
                    </ListItem>
                  ) : null}
                  {hasPermission('MANAGE_MWISE_DATABASE') ? (
                    <ListItem key={'new'} disablePadding>
                      <ListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => {
                          navigate(ROUTE.ADMIN_MDAC_OPTION2);
                          setDrawer(false);
                        }}
                      >
                        <ListItemIcon>
                          <CardMedia
                            component={'img'}
                            alt='M-Wise Database'
                            src={mwiseDB} // Replace with your actual image
                            sx={{ width: '35px', height: 'auto' }}
                          />
                        </ListItemIcon>
                        <ListItemText primary='M-Wise Database' />
                      </ListItemButton>
                    </ListItem>
                  ) : null}
                  {hasPermission('MWISE_FEEDBACK') || hasPermission('MW_ANALYTICS_REF_SOURCE') ? (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => setOpenMisslenious(!openMisslenious)}>
                          <ListItemIcon>
                            <CardMedia
                              component={'img'}
                              alt='Misc Database'
                              src={mislenious} // Replace with your actual image
                              sx={{ width: '30px', height: 'auto' }}
                            />
                          </ListItemIcon>
                          <ListItemText primary='Misc Database' />

                          {openMisslenious ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={openMisslenious} timeout='auto' unmountOnExit>
                        <List component='div' disablePadding>
                          {hasPermission('MWISE_FEEDBACK') ? (
                            <ListItem key={'FeedbackRating'} disablePadding>
                              <ListItemButton
                                sx={{ pl: 4 }}
                                onClick={() => {
                                  navigate(ROUTE.ADMIN_MDAC_FEEDBACK_RATING);
                                  setDrawer(false);
                                }}
                              >
                                <ListItemIcon>
                                  <CardMedia
                                    component={'img'}
                                    alt='M-Wise Feedback Ratings'
                                    src={review}
                                    sx={{ width: '24px', height: 'auto' }}
                                  />
                                </ListItemIcon>

                                <ListItemText primary='M-Wise Feedback Ratings' />
                              </ListItemButton>
                            </ListItem>
                          ) : null}
                          {hasPermission('MW_ANALYTICS_REF_SOURCE') ? (
                            <ListItem key={'sourceDashboard'} disablePadding>
                              <ListItemButton
                                sx={{ pl: 4 }}
                                onClick={() => {
                                  navigate(ROUTE.ADMIN_MDAC_SOURCE_DASHBOARD);
                                  setDrawer(false);
                                }}
                              >
                                <ListItemIcon>
                                  <CardMedia
                                    component={'img'}
                                    alt='Referrals Source'
                                    src={Referal}
                                    sx={{ width: '24px', height: 'auto' }}
                                  />
                                </ListItemIcon>
                                <ListItemText primary='Referrals Source' />
                              </ListItemButton>
                            </ListItem>
                          ) : null}
                        </List>
                      </Collapse>
                    </>
                  ) : null}
                </List>
              </Collapse>
              {/* Admin section: Practice Setup */}
              {hasPermission('USER_MANAGEMENT') ? (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => setOpenPracticeSetup(!openPracticeSetup)}>
                      <ListItemIcon>
                        <BusinessOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
                      </ListItemIcon>
                      <ListItemText primary='Practice Setup' />
                      {openPracticeSetup ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={openPracticeSetup} timeout='auto' unmountOnExit>
                    <List>
                      <ListItem sx={{ pl: 2 }} key={'UserManage'} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            navigate(ROUTE.ADMIN_PRACTICE_MANAGE_USERS_LIST);
                            setDrawer(false);
                          }}
                        >
                          <ListItemIcon>
                            <PeopleIcon sx={{ color: COLORS.DarkBlue }} />
                          </ListItemIcon>
                          <ListItemText primary='User Management' />
                        </ListItemButton>
                      </ListItem>
                      {/* MM-1168: Offices & Locations is a group - Manage Offices + Sync Calendar */}
                      <ListItem sx={{ pl: 2 }} key={'locations'} disablePadding>
                        <ListItemButton onClick={() => setOpenOfficesLocations(!openOfficesLocations)}>
                          <ListItemIcon>
                            <LocationOnIcon sx={{ color: COLORS.DarkBlue }} />
                          </ListItemIcon>
                          <ListItemText primary='Offices & Locations' />
                          {openOfficesLocations ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={openOfficesLocations} timeout='auto' unmountOnExit>
                        <List disablePadding>
                          <ListItem sx={{ pl: 4 }} key={'manageOffices'} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                navigate(ROUTE.ADMIN_PRACTICE_MANAGE_OFFICES_LOCATIONS);
                                setDrawer(false);
                              }}
                            >
                              <ListItemIcon>
                                <ApartmentOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
                              </ListItemIcon>
                              <ListItemText primary='Manage Offices' />
                            </ListItemButton>
                          </ListItem>
                          {hasPermission('VIEW_EHR_DETAILS') ? (
                            <ListItem sx={{ pl: 4 }} key={'syncCalendar'} disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  navigate(ROUTE.ADMIN_PRACTICE_SYNC_CALENDAR);
                                  setDrawer(false);
                                }}
                              >
                                <ListItemIcon>
                                  {/* MM-1198: manual sync of a provider's calendar with DrChrono */}
                                  <SyncOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
                                </ListItemIcon>
                                <ListItemText primary='Sync Calendar' />
                              </ListItemButton>
                            </ListItem>
                          ) : null}
                        </List>
                      </Collapse>
                    </List>
                  </Collapse>
                </>
              ) : null}

              {/* Admin section: System Monitoring */}
              {hasPermission('VIEW_SYSTEM_MONITORING') || hasPermission('VIEW_SYNC_JOBS') ? (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => setOpenJobs(!openJobs)}>
                      <ListItemIcon>
                        <CardMedia
                          component={'img'}
                          alt='JOBS'
                          src={healthMonitoring} // Replace with your actual image
                          sx={{ width: '30px', height: 'auto' }}
                        />
                      </ListItemIcon>
                      <ListItemText primary='System Monitoring' />
                      {openJobs ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={openJobs} timeout='auto' unmountOnExit>
                    <List component='div' disablePadding>
                      {/* System Monitoring sub-item: Application Health */}
                      <ListItem key={'applicationhealth'} disablePadding>
                        <ListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => {
                            navigate(ROUTE.APPLICATION_HEALTH);
                            setDrawer(false);
                          }}
                        >
                          <ListItemIcon>
                            <CardMedia
                              component={'img'}
                              alt='Application health'
                              src={health} // Replace with your actual image
                              sx={{ width: '24px', height: 'auto' }}
                            />
                          </ListItemIcon>
                          <ListItemText primary='Application Health' />
                        </ListItemButton>
                      </ListItem>
                    </List>
                    <List component='div' disablePadding>
                      {/* System Monitoring sub-item: DrChrono Failed Events */}
                      <ListItem key={'drchronojob'} disablePadding>
                        <ListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => {
                            navigate(ROUTE.ADMIN_JOBS_REPORT);
                            setDrawer(false);
                          }}
                        >
                          <ListItemIcon>
                            {/* MM-1198: sync arrows with a warning mark - DrChrono sync events that failed */}
                            <SyncProblemOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
                          </ListItemIcon>
                          <ListItemText primary='DrChrono Failed Events' />
                        </ListItemButton>
                      </ListItem>
                    </List>
                    {/* MM-1169: System Monitoring sub-item: Sync Jobs (calendar-sync endpoint permission) */}
                    {hasPermission('VIEW_EHR_DETAILS') ? (
                      <List component='div' disablePadding>
                        <ListItem key={'syncjobs'} disablePadding>
                          <ListItemButton
                            sx={{ pl: 4 }}
                            onClick={() => {
                              navigate(ROUTE.ADMIN_SYNC_JOBS);
                              setDrawer(false);
                            }}
                          >
                            <ListItemIcon>
                              {/* MM-1198: calendar with sync arrows - scheduled calendar syncs, not the DrChrono
                                  Failed Events icon */}
                              <EventRepeatOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
                            </ListItemIcon>
                            <ListItemText primary='Sync Jobs' />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    ) : null}
                  </Collapse>
                </>
              ) : null}
            </>
          ) : (
            /* Non-admin section: standard menu links */
            menuItems.map((text) => {
              if (text.label === 'Knowledge Base') {
                return (
                  <ListItem key={text.label} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        setTimeout(() => {
                          window.open('https://kb.mindweal.com/en/publickb', '_blank');
                        });
                        setDrawer(false);
                      }}
                    >
                      <ListItemIcon>{text.icon}</ListItemIcon>
                      <ListItemText primary={text.label} />
                    </ListItemButton>
                  </ListItem>
                );
              } else {
                return (
                  <ListItem key={text.label} disablePadding>
                    <ListItemButton
                      disabled={text.disabled}
                      onClick={() => {
                        navigate(text.link, {
                          state: text.state ?? {}, // ✅ PASS STATE HERE
                        });
                        setDrawer(false);
                      }}
                    >
                      <ListItemIcon>{text.icon}</ListItemIcon>
                      <ListItemText primary={text.label} />
                    </ListItemButton>
                  </ListItem>
                );
              }
            })
          )}
        </List>
      </Grid>
      <Grid item>
        {/* Footer section (MM-1123): Add Child, Switch Child, Settings (MM-1131) and Log Out, set apart */}
        <Divider />
        <List sx={{ paddingTop: 'auto' }}>
          {!user?.isAdmin ? (
            <ListItem disablePadding>
              <ListItemButton
                disabled={siblings?.length >= 15}
                onClick={() => {
                  navigate(`${ROUTE.PROFILE}?addSiblings=true`, { state: { patientId: user?.patientId } });
                  setDrawer(false);
                }}
              >
                <ListItemIcon>
                  <GroupAddIcon sx={{ color: COLORS.DarkBlue }} />
                </ListItemIcon>
                <ListItemText primary='Add Child' />
              </ListItemButton>
            </ListItem>
          ) : null}
          {/* MM-1123: Switch Child is always listed for a guardian, disabled until there is a child to switch to */}
          {!user?.isAdmin ? (
            <Tooltip title={switchChildDisabledReason} arrow placement='right'>
              {/* span: a disabled button emits no mouse events, so the tooltip needs a wrapper */}
              <span style={{ display: 'block' }}>
                <ListItem disablePadding>
                  <ListItemButton
                    disabled={switchChildDisabledReason !== ''}
                    aria-haspopup='menu'
                    aria-expanded={Boolean(siblingMenuAnchor)}
                    onClick={(e) => setSiblingMenuAnchor(e.currentTarget)}
                  >
                    <ListItemIcon>
                      <SwapHorizIcon sx={{ color: COLORS.DarkBlue }} />
                    </ListItemIcon>
                    <ListItemText primary='Switch Child' />
                    <ChevronRightIcon />
                  </ListItemButton>
                </ListItem>
              </span>
            </Tooltip>
          ) : null}
          {/* MM-1123: siblings open in a pop-out menu beside Switch Child, so the drawer never grows */}
          {canSwitchSibling ? (
            <Menu
              anchorEl={siblingMenuAnchor}
              open={Boolean(siblingMenuAnchor)}
              onClose={() => setSiblingMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { minWidth: 220, maxWidth: 280, borderRadius: '12px' } }}
              MenuListProps={{ dense: true }}
            >
              {siblings.map((sibling: SiblingProfile) => {
                const isActive = sibling.id === activeSibling?.id;
                return (
                  <MenuItem
                    key={sibling.id}
                    selected={isActive}
                    onClick={() => {
                      setSiblingMenuAnchor(null);
                      setDrawer(false);
                      if (!isActive) {
                        switchSibling(sibling);
                      }
                    }}
                    sx={{ gap: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <SiblingAvatar sibling={sibling} siblings={siblings} size={26} />
                    </ListItemIcon>
                    <ListItemText
                      primary={formatSiblingName(sibling)}
                      title={formatSiblingName(sibling)}
                      sx={{ minWidth: 0 }}
                      primaryTypographyProps={{ noWrap: true, fontWeight: isActive ? 600 : 400 }}
                    />
                    {isActive ? <CheckIcon fontSize='small' sx={{ color: COLORS.DarkBlue, flexShrink: 0 }} /> : null}
                  </MenuItem>
                );
              })}
            </Menu>
          ) : null}
          {/* MM-1131: Settings (Change Password / Change PIN) directly above Log Out */}
          <SettingsMenuItem
            hidden={Boolean(user?.isAdmin)}
            onOpen={() => {
              navigate(ROUTE.PROFILE_SETTINGS);
              setDrawer(false);
            }}
          />
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate(`${ROUTE.SIGN_OUT}`)}>
              <ListItemIcon>
                <CardMedia component={'img'} alt='logout' src={logout} sx={{ width: '20px', height: 'auto' }} />
              </ListItemIcon>
              <ListItemText primary={'Log Out'} />
            </ListItemButton>
          </ListItem>
        </List>
      </Grid>
    </Grid>
  );

  const toggleDrawer = (toggle: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawer(toggle);
  };

  return (
    <>
      <SwipeableDrawer
        anchor={'left'}
        open={drawer}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        sx={{ zIndex: 1300 }}
      >
        {menulist()}
      </SwipeableDrawer>

      <AppBar elevation={1} position='sticky' color='inherit'>
        <Container maxWidth='xl'>
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <IconButton
              size='large'
              onClick={toggleDrawer(!drawer)}
              edge='start'
              color='inherit'
              aria-label='menu'
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>

            <CardMedia
              component='img'
              image={logo}
              sx={{
                width: 'auto',
                height: '35px',
                margin: { md: 'auto' },
                display: { xs: 'block', sm: 'none' },
              }}
              alt='image'
            />
            <CardMedia
              component='img'
              image={logoFull}
              sx={{
                width: 'auto',
                height: '35px',
                margin: { md: 'auto' },
                display: { xs: 'none', sm: 'block' },
              }}
              alt='image'
            />
            {user?.isAdmin && hasPermission('VIEW_PATIENTS') ? (
              <Box sx={{ mr: '24px' }}>
                <SearchBox />
              </Box>
            ) : null}
            <IconButton
              size='small'
              aria-label='account of current user'
              aria-controls='menu-appbar'
              aria-haspopup='true'
              onClick={() => navigate(`${user.isAdmin ? ROUTE.ADMIN_PROFILE : ROUTE.DASHBOARD}`)}
              color='inherit'
              sx={{
                background: '#F2F4F7',
                padding: '0',
                width: '54px',
                height: '54px',
                borderRadius: '50%',
              }}
            >
              <Typography variant='h3' sx={{ color: COLORS.Gray600 }}>
                {user?.firstName?.charAt(0).toUpperCase()}
                {user?.lastName?.charAt(0).toUpperCase()}
              </Typography>
              {/* <AccountCircle /> */}
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>
    </>
  );
};

export default Header;
